import {DatePipe, NgOptimizedImage} from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	InputSignal,
	OutputEmitterRef,
	Signal,
	WritableSignal,
	computed,
	inject,
	input,
	linkedSignal,
	output,
} from '@angular/core';
import {Router} from '@angular/router';
import {TooltipModule} from '@syncfusion/ej2-angular-popups';
import {ContentComponent} from '../../../../../shared/elements/content/content.component';
import {FormButtonComponent} from '../../../../../shared/elements/form-button/form-button.component';
import {HeadlineComponent} from '../../../../../shared/elements/headline/headline.component';
import {IconBadgeComponent} from '../../../../../shared/elements/icon-badge/icon-badge.component';
import {RichTextPlainPipe} from '../../../../../shared/pipes/rich-text-plain.pipe';
import {TranslatePipe} from '../../../../../shared/pipes/translate.pipe';
import {ProfileService} from '../../../../../shared/services/profile.service';
import {FeedbackUserInstanceCardAction} from '../../../../../shared/types/feedback-types/feedbackUserInstanceCardAction.type';
import {FeedbackUserInstanceCardActionMode} from '../../../../../shared/types/feedback-types/feedbackUserInstanceCardActionMode.type';
import {FeedbackInstanceListResource} from '../../../../../shared/types/generated/feedbackInstanceListResource';

@Component({
	selector: 'app-feedback-user-instance-card',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: {
		'[class.feedback-line-card-host]': 'lineMode()',
	},
	imports: [
		DatePipe,
		NgOptimizedImage,
		TooltipModule,
		IconBadgeComponent,
		HeadlineComponent,
		ContentComponent,
		FormButtonComponent,
		TranslatePipe,
		RichTextPlainPipe,
	],
	template: `
		@if (lineMode()) {
			<div [class]="cardClassName()">
				<div class="card-body feedback-line-card-body">
					<div class="fixed-width-name d-flex align-items-center gap-2">
						<app-icon-badge
							[style]="'default'"
							[icon]="'cycle'"
							[fontSize]="'fs-3'"
							[class]="iconClassName()"
						>
						</app-icon-badge>
						<div class="flex-grow-1 min-width-0">
							<app-content
								[class]="'line-card-title'"
								[fontSize]="'fs-default'"
								[fontColor]="'text-primary'"
								[fontDecoration]="'fw-bold'"
								[truncateLines]="2"
							>
								{{ title() }}
							</app-content>
						</div>
						<div class="vr line-card-divider"></div>
					</div>

					<div class="details-container lh-1">
						<div class="detail-item d-flex flex-column">
							<app-content [class]="'detail-label'" [fontSize]="'fs-default'" [fontColor]="'text-muted'" [truncateLines]="1">{{ 'FEEDBACKQUESTIONS' | translate }}:</app-content>
							<app-content [class]="'detail-value'" [fontSize]="'fs-small'" [fontColor]="'text-muted'" [fontDecoration]="'fw-bold'" [truncateLines]="1">
								{{ questionCount() }}
							</app-content>
						</div>
						<div class="detail-item d-flex flex-column">
							<app-content [class]="'detail-label'" [fontSize]="'fs-default'" [fontColor]="'text-muted'" [truncateLines]="1">{{ 'WORKFLOWSTAGEPROGRESS' | translate }}:</app-content>
							<app-content [class]="'detail-value'" [fontSize]="'fs-small'" [fontColor]="'text-muted'" [fontDecoration]="'fw-bold'" [truncateLines]="1">
								{{ lineProgressPercent() }}%
							</app-content>
						</div>
						<div class="detail-item d-flex flex-column">
							<app-content [class]="'detail-label'" [fontSize]="'fs-default'" [fontColor]="'text-muted'" [truncateLines]="1">{{ 'FEEDBACKCOMPLETED' | translate }}:</app-content>
							<app-content [class]="'detail-value'" [fontSize]="'fs-small'" [fontColor]="'text-muted'" [fontDecoration]="'fw-bold'" [truncateLines]="1">
								{{ submittedAt() === null ? '-' : (submittedAt() | date) }}
							</app-content>
						</div>
						<div class="detail-item d-flex flex-column">
							<app-content [class]="'detail-label'" [fontSize]="'fs-default'" [fontColor]="'text-muted'" [truncateLines]="1">{{ 'FEEDBACKTYPE' | translate }}:</app-content>
							<app-content [class]="'detail-value ' + privacyClassName()" [fontSize]="'fs-small'" [fontDecoration]="'fw-bold'" [truncateLines]="1">
								{{ instance().form.anonymous ? ('FEEDBACKANONYMOUS' | translate) : 'Public' }}
							</app-content>
						</div>
						<div class="detail-item d-flex flex-column">
							<app-content [class]="'detail-label'" [fontSize]="'fs-default'" [fontColor]="'text-muted'" [truncateLines]="1">{{ 'FEEDBACKPRIORITY' | translate }}:</app-content>
							<app-content [class]="'detail-value feedback-priority' + (instance().form.mandatory ? ' text-primary': '')" [fontSize]="'fs-small'" [fontDecoration]="'fw-bold'" [truncateLines]="1">
								{{ instance().form.mandatory ? ('FEEDBACKMANDATORY' | translate) : ('FEEDBACKOPTIONAL' | translate) }}
							</app-content>
						</div>
					</div>

					<div class="line-card-actions d-flex align-items-center gap-2">
						<div class="mobile-details-container">
							<ejs-tooltip
								[target]="mobileInfoTarget()"
								opensOn="Click"
								position="BottomCenter"
								[animation]="'Fade'"
								[windowCollision]="'flip'"
							>
								<button
									type="button"
									[class]="'btn btn-link p-0 text-muted ' + mobileInfoClass()"
									[attr.aria-label]="'FEEDBACKSHOWINFOMENU' | translate"
								>
									<app-icon-badge
										[style]="'default'"
										[icon]="'info'"
										[fontSize]="'fs-4'"
										[class]="'cursor-pointer'"
									>
									</app-icon-badge>
								</button>
								<ng-template #content>
									<div class="width-max-content">
										<div class="my-1 d-flex gap-1">
											<app-content [fontSize]="'fs-default'" [fontColor]="'text-muted'">{{ 'FEEDBACKQUESTIONS' | translate }}:</app-content>
											<app-content [fontSize]="'fs-small'" [fontColor]="'text-muted'" [fontDecoration]="'fw-bold'">
												{{ questionCount() }}
											</app-content>
										</div>
										<div class="my-1 d-flex gap-1">
											<app-content [fontSize]="'fs-default'" [fontColor]="'text-muted'">{{ 'WORKFLOWSTAGEPROGRESS' | translate }}:</app-content>
											<app-content [fontSize]="'fs-small'" [fontColor]="'text-muted'" [fontDecoration]="'fw-bold'">
												{{ lineProgressPercent() }}%
											</app-content>
										</div>
										<div class="my-1 d-flex gap-1">
											<app-content [fontSize]="'fs-default'" [fontColor]="'text-muted'">{{ 'FEEDBACKCOMPLETED' | translate }}:</app-content>
											<app-content [fontSize]="'fs-small'" [fontColor]="'text-muted'" [fontDecoration]="'fw-bold'">
												{{ submittedAt() === null ? '-' : (submittedAt() | date) }}
											</app-content>
										</div>
										<div class="my-1 d-flex gap-1">
											<app-content [fontSize]="'fs-default'" [fontColor]="'text-muted'">{{ 'FEEDBACKTYPE' | translate }}:</app-content>
											<app-content [fontSize]="'fs-small'" [fontDecoration]="'fw-bold'" [class]="privacyClassName()">
												{{ instance().form.anonymous ? ('FEEDBACKANONYMOUS' | translate) : 'Public' }}
											</app-content>
										</div>
										<div class="my-1 d-flex gap-1">
											<app-content [fontSize]="'fs-default'" [fontColor]="'text-muted'">{{ 'FEEDBACKPRIORITY' | translate }}:</app-content>
											<app-content [fontSize]="'fs-small'" [fontDecoration]="'fw-bold'" [class]="'feedback-priority'">
												{{ instance().form.mandatory ? ('FEEDBACKMANDATORY' | translate) : ('FEEDBACKOPTIONAL' | translate) }}
											</app-content>
										</div>
									</div>
								</ng-template>
							</ejs-tooltip>
						</div>
						@if (hasStatisticsAccess()) {
							<div class="dropdown">
								<button
									type="button"
									class="border-0 bg-transparent p-0 d-flex align-items-center"
									data-bs-toggle="dropdown"
									aria-expanded="false"
									[attr.aria-label]="'SRMOREOPTIONS' | translate"
								>
									<app-icon-badge
										[style]="'default'"
										[icon]="'more_horiz'"
										[fontSize]="'fs-4'"
										[fontColor]="'text-muted'"
									>
									</app-icon-badge>
								</button>
								<ul class="dropdown-menu dropdown-menu-end">
									<li>
										<button type="button" class="dropdown-item" (click)="navigateToInstaceStatistics(instance())">
											{{ 'FEEDBACKSHOWSTATISTICSMENU' | translate }}
										</button>
									</li>
								</ul>
							</div>
						}
					</div>
				</div>
			</div>
		} @else {
			<div [class]="cardClassName()">
			<div class="card-body flex-grow-1 p-card pt-1">
				<div class="heading-menu-container d-flex justify-content-between align-items-center">
					<div class="d-flex flex-wrap align-items-center gap-2">
						@for (badge of badges(); track badge.labelKey) {
							<span [class]="badge.cssClass">{{ badge.labelKey | translate }}</span>
						}
					</div>
				</div>
				<div class="title-container d-flex align-items-center my-2">
					@if (imageSrc() !== null && !imageLoadFailed()) {
						<img
							[ngSrc]="imageSrc()!"
							[alt]="imageAlt()"
							[width]="55"
							[height]="55"
							[disableOptimizedSrcset]="isGifPreview()"
							class="card-img me-2"
							(error)="imageLoadFailed.set(true)"
							(load)="imageLoadFailed.set(false)"
						>
					} @else {
						<div class="icon-placeholder border me-2">
							<app-icon-badge
								[style]="'default'"
								[icon]="'cycle'"
								[fontSize]="'fs-3'"
								[class]="iconClassName()"
							>
							</app-icon-badge>
						</div>
					}
					<app-headline [title]="title()" [type]="'h6'" [class]="'fw-bold truncate-text-three-lines'"></app-headline>
				</div>
				<div class="description-container mb-2">
					<app-content [fontSize]="'fs-default'" [fontColor]="'text-muted'" [truncateLines]="3">
						{{ description() | richTextPlain }}
					</app-content>
				</div>
				@if (showProgress()) {
					<div class="progress-container d-flex align-items-center justify-content-between mb-2">
						<div class="d-flex align-items-center gap-1">
							<app-content [fontSize]="'fs-small'" [fontColor]="'text-muted'" [fontDecoration]="'fw-bold'">
								{{ progressLabel() }}
							</app-content>
							<app-content [fontSize]="'fs-verysmall'" [fontColor]="'text-primary'" [fontDecoration]="'fw-bold'">
								{{ progressPercent() }}%
							</app-content>
						</div>
						<div class="d-flex align-items-center gap-1">
							<app-content [fontSize]="'fs-small'" [fontColor]="'text-muted'" [fontDecoration]="'fw-bold'">
								{{ questionsLabel() }}
							</app-content>
							<app-content [fontSize]="'fs-verysmall'" [fontColor]="'text-primary'" [fontDecoration]="'fw-bold'">
								{{ questionCount() }}
							</app-content>
						</div>
					</div>
				}
				<div class="bottom-container d-flex align-items-center justify-content-center">
					@if (showActionButton()) {
						<app-form-button
							[text]="actionButtonKey() | translate"
							[cssClass]="actionButtonClassName()"
							[disabled]="actionDisabled()"
							(click)="actionSelected.emit({action: action(), instance: instance()})"
						>
						</app-form-button>
					} @else {
						<div class="d-flex align-items-center flex-wrap gap-3">
							<div class="d-flex align-items-center gap-1">
								<app-icon-badge
									[style]="'default'"
									[icon]="'event_available'"
									[fontSize]="'fs-6'"
									[fontColor]="'text-muted'"
								>
								</app-icon-badge>
								<app-content [fontSize]="'fs-verysmall'" [fontColor]="'text-muted'" [fontDecoration]="'fw-bold'">
									{{ instance().starts_at | date }}
								</app-content>
							</div>
							<div class="d-flex align-items-center gap-1">
								<app-icon-badge
									[style]="'default'"
									[icon]="'event_busy'"
									[fontSize]="'fs-6'"
									[fontColor]="'text-muted'"
								>
								</app-icon-badge>
								<app-content [fontSize]="'fs-verysmall'" [fontColor]="'text-muted'" [fontDecoration]="'fw-bold'">
									{{ instance().ends_at === null ? ('FEEDBACKNOEND' | translate) : (instance().ends_at | date) }}
								</app-content>
							</div>
							<div class="d-flex align-items-center gap-1">
								<app-icon-badge
									[style]="'default'"
									[icon]="'fact_check'"
									[fontSize]="'fs-6'"
									[fontColor]="'text-muted'"
								>
								</app-icon-badge>
								<app-content [fontSize]="'fs-verysmall'" [fontColor]="'text-muted'" [fontDecoration]="'fw-bold'">
									{{ answeredQuestionsCount() }} / {{ instance().form.questions_count }}
								</app-content>
							</div>
						</div>
					}
				</div>
			</div>
		</div>
		}
	`,
	styleUrls: ['./feedback-user-instance-card.component.scss'],
})
export class FeedbackUserInstanceCardComponent {
	private profileService: ProfileService = inject(ProfileService);
	private router: Router = inject(Router);

	public instance: InputSignal<FeedbackInstanceListResource> = input.required();
	public lineMode: InputSignal<boolean> = input(false);
	public lineInfoId: InputSignal<number | null> = input<number | null>(null);
	public submittedAt: InputSignal<string | null> = input<string | null>(null);
	public actionMode: InputSignal<FeedbackUserInstanceCardActionMode> = input<FeedbackUserInstanceCardActionMode>('auto');
	public showActionButton: InputSignal<boolean> = input(true);
	public showProgress: InputSignal<boolean> = input(true);
	public useAnonymousBorder: InputSignal<boolean> = input(false);
	public progressLabel: InputSignal<string> = input('Progress:');
	public questionsLabel: InputSignal<string> = input('Questions:');
	public startButtonKey: InputSignal<string> = input('FEEDBACKSTARTBUTTON');
	public continueButtonKey: InputSignal<string> = input('FEEDBACKCONTINUEBUTTON');
	public startButtonCssClass: InputSignal<string> = input('e-primary e-small mt-1 fs-default fw-semibold');
	public continueButtonCssClass: InputSignal<string> = input('e-warning e-small mt-1 fs-default fw-semibold');
	public actionDisabled: InputSignal<boolean> = input(false);

	public infoSelected: OutputEmitterRef<FeedbackInstanceListResource> = output<FeedbackInstanceListResource>();
	public statisticsSelected: OutputEmitterRef<FeedbackInstanceListResource> = output<FeedbackInstanceListResource>();
	public actionSelected: OutputEmitterRef<{
		action: FeedbackUserInstanceCardAction;
		instance: FeedbackInstanceListResource;
	}> = output();

	public title: Signal<string> = computed(() => {
		const form = this.instance().form;
		return this.profileService.profile()?.user.auto_translation ? (form.title_translated ?? form.title) : form.title;
	});
	public description: Signal<string | null> = computed(() => {
		const form = this.instance().form;
		return this.profileService.profile()?.user.auto_translation ? (form.description_translated ?? form.description) : form.description;
	});
	public imageSrc: Signal<string | null> = computed(() => this.instance().form.preview?.path || null);
	public imageLoadFailed: WritableSignal<boolean> = linkedSignal(() => {
		this.imageSrc();
		return false;
	});
	public imageAlt: Signal<string> = computed(() => this.instance().form.preview?.filename || this.title());
	public isGifPreview: Signal<boolean> = computed(() =>
		/\.gif$/i.test(this.instance().form.preview?.filename.trim() ?? '')
	);
	public hasUnsubmittedAttempt: Signal<boolean> = computed(() =>
		this.instance().unsubmitted_attempt !== null && this.instance().unsubmitted_attempt !== undefined
	);
	public answeredQuestionsCount: Signal<number> = computed(() =>
		this.instance().unsubmitted_attempt?.answered_questions_count ?? 0
	);
	public questionCount: Signal<number| null> = computed(() => {
		return this.instance().form?.questions_count !== undefined ? this.instance().form.questions_count as number : null;
	});
	public progressPercent: Signal<number> = computed(() => {
		const questionCount = this.questionCount();
		if (questionCount === 0 || questionCount === null) {
			return 0;
		}

		return Math.min(100, Math.round((this.answeredQuestionsCount() / questionCount) * 100));
	});
	public lineProgressPercent: Signal<number> = computed(() => this.lineMode() ? 100 : this.progressPercent());
	public action: Signal<FeedbackUserInstanceCardAction> = computed(() => {
		const actionMode = this.actionMode();
		if (actionMode !== 'auto') {
			return actionMode;
		}

		return this.hasUnsubmittedAttempt() ? 'continue' : 'start';
	});
	public actionButtonKey: Signal<string> = computed(() =>
		this.action() === 'continue' ? this.continueButtonKey() : this.startButtonKey()
	);
	public actionButtonClassName: Signal<string> = computed(() =>
		this.action() === 'continue' ? this.continueButtonCssClass() : this.startButtonCssClass()
	);
	public badges: Signal<{labelKey: string; cssClass: string}[]> = computed(() => {
		const form = this.instance().form;
		const badges = [
			form.mandatory
				? {labelKey: 'FEEDBACKMANDATORY', cssClass: 'badge bg-primary'}
				: {labelKey: 'FEEDBACKOPTIONAL', cssClass: 'badge bg-transparent text-muted border'},
		];

		if (form.anonymous) {
			badges.push({labelKey: 'FEEDBACKANONYMOUS', cssClass: 'badge bg-secondary'});
		}

		return badges;
	});
	public cardClassName: Signal<string> = computed(() => {
		const form = this.instance().form;
		if (this.lineMode()) {
			return [
				'card feedback-line-card p-0 mb-3',
				form.mandatory ? 'mandatory' : '',
				form.anonymous ? 'anonym' : '',
			].filter(Boolean).join(' ');
		}

		return [
			'card h-100 d-flex flex-column',
			form.mandatory ? 'mandatory' : '',
			this.useAnonymousBorder() && form.anonymous ? 'anonym' : '',
			this.hasUnsubmittedAttempt() ? 'incomplete' : '',
		].filter(Boolean).join(' ');
	});
	public iconClassName: Signal<string> = computed(() => {
		const form = this.instance().form;
		if (this.lineMode()) {
			if(form.mandatory) {
				return 'text-primary';
			} else {
				return 'text-muted';
			}
		} else {
			return 'text-primary';
		}
	});

	public privacyClassName: Signal<string> = computed(() => this.instance().form.anonymous ? 'type-anonym' : 'type-public');
	public mobileInfoClass: Signal<string> = computed(() => `feedback-line-info-${this.lineInfoId() ?? this.instance().id}`);
	public mobileInfoTarget: Signal<string> = computed(() => `.${this.mobileInfoClass()}`);
	public hasStatisticsAccess: Signal<boolean> = computed(() =>
		this.lineMode()
		&& this.lineInfoId() !== null
		&& this.instance().form.show_statistics_to_participants
	);

	public navigateToInstaceStatistics(instace: FeedbackInstanceListResource): void {
		const attemptId = this.lineInfoId();
		if (attemptId === null) {
			return;
		}

		this.statisticsSelected.emit(instace);
		void this.router.navigate(['/feedback', 'statistics', 'attempts', attemptId]);
	}
}
